// The data manifest for THIS dashboard: every table the app loads into DuckDB, where it comes
// from, and the DDL around it. All project SQL for the load lives here; `@rdrive/data-loader`
// (`createDataLoader`, built over this in `store/index.js`) holds none — PLATFORM.md §3.2 rule 2.
//
// Boot tables (no `lazy`) are what `loader.load()` fetches, in parallel, when App/setup.js runs
// the data load: the same 13 requests, paths, formats and table names as the inline
// `apiRequests` array this replaced, in the same order. `{projectCode}` is filled from the
// store's live settings at load time. Lazy tables are loaded by the page that needs them, with
// `loader.ensure(name)`.

/**
 * `status_history_temporal` is DERIVED, not fetched: one row per status interval
 * (validFrom/validTo) from the raw `status_history` parquet joined to `items`. Built by
 * `status_history`'s postLoad, so it exists exactly when the raw table has landed. There is
 * deliberately no boot-time placeholder: nothing reads it before ActivityPage has ensured
 * `status_history` (the status-history filter context is inert until Activity initialises it),
 * and the old stub had a stale schema and broke the full-reload cycle with "already exists".
 * Copied from ActivityPage, where it was built before the loader existed. `createdByTeamId` /
 * `createdByUserId` come from the status-history row (who made the change), as in
 * issue-analysis; they used to come from `items` (who created the item), which put a
 * subcontractor's "Fixed" under the team that raised the item on Activity By Team.
 */
const STATUS_HISTORY_TEMPORAL_SELECT = `
      WITH ordered AS (
        SELECT
            sht.subItemId,
            sht.statusId,
            i.locationId,
            -- who made the status change, not who created the item
            sht.createdByTeamId,
            sht.createdByUserId,
            i.profileId,
            i.SubItemTemplateId                  AS subItemTemplateId,
            sht.createdDate                      AS validFrom,
            LEAD(sht.createdDate)
              OVER (PARTITION BY sht.subItemId ORDER BY sht.createdDate)
                                                  AS validTo
        FROM status_history sht
        JOIN items i USING (subItemId)             -- add the three columns
      )
      SELECT
          subItemId as subItemId,
          statusId as statusId,
          locationId as locationId,
          createdByTeamId as createdByTeamId,
          createdByUserId as createdByUserId,
          profileId as profileId,
          subItemTemplateId as subItemTemplateId,
          validFrom,
          validTo,
          CASE
              WHEN validTo IS NOT NULL
              THEN DATEDIFF('minute', validFrom, validTo)
              ELSE DATEDIFF('minute', validFrom,
                            CAST(CURRENT_TIMESTAMP AS TIMESTAMP))
          END AS durationMinutes
      FROM ordered
      ORDER BY subItemId, validFrom   -- keeps IDs highly compressible
    `;

/**
 * Re-ranks `status_templates.sort` once the boot tables are in: drafts first, then initial
 * statuses, terminal ones last, and terminal-but-not-passed pinned at 50. Not idempotent — it
 * runs again on every full reload, exactly as it did inline in App/setup.js.
 */
const STATUS_TEMPLATES_SORT = `
        UPDATE status_templates
        SET sort = 
            CASE 
                WHEN isTerminal AND NOT isPassed THEN 50
                ELSE sort + 
                    CASE WHEN isDraft THEN -100 ELSE 0 END +
                    CASE WHEN isInitial THEN -50 ELSE 0 END +
                    CASE WHEN isTerminal THEN 100 ELSE 0 END
            END;
      `;

/**
 * `project-item-templates` gained a `current` column in a newer API version — whether the
 * template is enabled in this project — and older APIs send none. Adds it as `true` when it is
 * missing (an old API's templates all count as enabled), so SQL can read `it."current"` on
 * either version: the project summary lists a category when one of its templates is current
 * or it still has items (useCategorySummary). Rows are never filtered here — a disabled
 * template stays joinable for the items already created from it. Idempotent (the full reload
 * re-runs it). `current` is quoted: it is a keyword.
 */
const ITEM_TEMPLATES_CURRENT = `ALTER TABLE item_templates ADD COLUMN IF NOT EXISTS "current" BOOLEAN DEFAULT true`;

export const manifest = {
  tables: [
    { name: 'items', path: '/api/{projectCode}/reporting/sub-items/latest/', format: 'parquet' },
    {
      name: 'item_templates',
      path: '/api/{projectCode}/reporting/project-item-templates',
      format: 'parquet',
      postLoad: ITEM_TEMPLATES_CURRENT,
    },
    { name: 'categories', path: '/api/categories', format: 'parquet' },
    { name: 'status_templates', path: '/api/{projectCode}/reporting/items/item-status-templates', format: 'parquet' },
    { name: 'drawings', path: '/api/{projectCode}/drawings', format: 'parquet' },
    { name: 'locations', path: '/api/{projectCode}/locations', format: 'parquet' },
    { name: 'teams', path: '/api/{projectCode}/teams', format: 'parquet' },
    { name: 'team_users', path: '/api/{projectCode}/bi/teams', format: 'parquet' },
    { name: 'users', path: '/api/{projectCode}/teams/users', format: 'parquet' },
    { name: 'current_user', path: '/api/users/current', format: 'json' },
    { name: 'form_templates', path: '/api/{projectCode}/bi/form-templates', format: 'parquet' },
    { name: 'permission_rules', path: '/api/{projectCode}/users/current/permission-rules', format: 'json' },
    { name: 'company', path: '/api/company', format: 'json' },

    // Lazy: only the Activity page needs it, on its first visit (ActivityPage/setup.js)
    {
      name: 'status_history',
      path: '/api/{projectCode}/reporting/items/status-history-short',
      format: 'parquet',
      lazy: true,
      postLoad: ({ duckdb }) =>
        duckdb.createTableFromQuery('status_history_temporal', STATUS_HISTORY_TEMPORAL_SELECT, { replace: true }),
    },
  ],
  ddl: {
    post: [STATUS_TEMPLATES_SORT],
  },
};
